import { getUpdatedLocaleForURLAfterRegionSwitch } from '@src/components/locale/utils'
import { DEFAULT_LOCALE_PREFIX, LOCALE_COOKIE } from '@src/config/localisation/locale-config.constants'
import type { TAbsoluteLocale, TRegionDetail } from '@src/config/localisation/locale-config.types'
import regionDetailsHolder from '@src/quince-site-common/src/data-holders/regionDetails.holder'
import { STORE_PROGRAM_ID_KEY } from '@src/quince-site-common/src/services/cartCheckoutService/constants'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { allUrlLocales, getStandardLocale, parseLocale } from '@src/utils/i18n.utils'
import { getLocaleFromUrlPath } from '@src/utils/urlLocale.utils'

const CHECKOUT_PATH = '/checkout'
const CHECKOUT_PATH_NEW = '/checkout/v2'

export const getLocalePrefix = (): string => {
  try {
    const locale = getCookie(LOCALE_COOKIE) as TAbsoluteLocale
    if (locale) {
      const localePrefix = getStandardLocale(locale)
      return localePrefix
    }
    return getLocaleFromUrlPath(DEFAULT_LOCALE_PREFIX)
  } catch (error) {
    console.error('Error getting locale prefix:', error)
    return DEFAULT_LOCALE_PREFIX
  }
}

/**
 * Creates a locale-prefixed checkout URL
 * @param baseUrl - The base checkout URL
 * @param checkoutId - The checkout ID
 * @param checkoutAuthCode - The checkout auth code
 * @returns The complete checkout URL with locale prefix (only if different from default)
 */
export const createLocalePrefixedCheckoutUrl = (
  baseUrl: string,
  checkoutId: string,
  checkoutAuthCode: string,
  showNewDesign: boolean,
  isI18nPreviewEnabled: boolean,
  isB2BFlow: boolean,
  storeProgramId?: string,
): string => {
  const locale = (getCookie(LOCALE_COOKIE) as TAbsoluteLocale) || DEFAULT_LOCALE_PREFIX
  const regionDetails = regionDetailsHolder.getRegionDetails() as TRegionDetail

  const updatedLocale = getUpdatedLocaleForURLAfterRegionSwitch(locale, regionDetails)
  const url = new URL(baseUrl)

  const path = showNewDesign && !isB2BFlow ? CHECKOUT_PATH_NEW : CHECKOUT_PATH

  const updatedPath = isB2BFlow ? `${path}/b2b-checkout-form` : path

  // Only add locale prefix if it's different from the default locale
  if (updatedLocale === '' || !isI18nPreviewEnabled) {
    url.pathname = updatedPath
  } else {
    url.pathname = `/${updatedLocale}${updatedPath}`
  }

  url.searchParams.set('checkoutId', checkoutId)
  url.searchParams.set('checkoutAuthCode', checkoutAuthCode)
  if (isB2BFlow && storeProgramId) {
    url.searchParams.set(STORE_PROGRAM_ID_KEY, storeProgramId)
  }

  return url.href
}

/**
 * Returns true if the checkout redirect URL is for a different region (country) than the current locale.
 * When true, the app should redirect to cart after login instead of checkout.
 */
export const isCheckoutUrlForDifferentRegion = (checkoutRedirectUrl: string): boolean => {
  try {
    const url = new URL(checkoutRedirectUrl)
    if (!url || typeof url.pathname !== 'string') return false
    const pathname = url.pathname
    const firstSegment = pathname
      .split('/')
      .find(s => s !== '')
      ?.toLowerCase()
    const redirectUrlLocale =
      firstSegment && allUrlLocales.includes(firstSegment) ? firstSegment : DEFAULT_LOCALE_PREFIX
    const currentLocale = getLocalePrefix().toLowerCase()
    const redirectCountry = parseLocale(redirectUrlLocale as TAbsoluteLocale).country
    const currentCountry = parseLocale(currentLocale as TAbsoluteLocale).country
    return redirectCountry !== currentCountry
  } catch {
    return false
  }
}

/**
 * Rewrites a checkout redirect URL so its path uses the current locale.
 * Used after login when the redirecturi may point to a different region (e.g. after region switch).
 * Prevents checkout/middleware from redirecting to cart on region mismatch.
 * @param checkoutRedirectUrl - Full checkout URL from redirecturi (same-origin or checkout subdomain)
 * @returns URL string with path updated to current locale, or null if parsing/rewrite fails
 */
export const rewriteCheckoutUrlToCurrentLocale = (checkoutRedirectUrl: string): string | null => {
  try {
    const url = new URL(checkoutRedirectUrl)
    if (!url || typeof url.pathname !== 'string') return null
    const pathname = url.pathname
    const parts = pathname.split('/').filter(Boolean)
    const firstSegment = parts.length > 0 ? parts[0] : undefined
    const pathWithoutLocale =
      firstSegment && allUrlLocales.includes(firstSegment.toLowerCase()) ? '/' + parts.slice(1).join('/') : pathname

    const currentLocale = getLocalePrefix()
    const localeSegment = currentLocale ? currentLocale.toLowerCase() : ''
    const path = pathWithoutLocale ?? pathname
    const newPathname = localeSegment ? `/${localeSegment}${path}` : path
    url.pathname = newPathname
    return url.toString()
  } catch {
    return null
  }
}
